/** Small fetch wrapper: JSON in, JSON out, errors as messages. */
export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers: opts.body ? { 'content-type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
    signal: opts.signal,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  return data as T;
}

/**
 * Is this seat/viewer link still valid? true / false, or throws when the
 * server cannot be reached (so the caller keeps retrying).
 */
export async function linkValid(token: string): Promise<boolean> {
  const res = await fetch(`/api/whoami?t=${encodeURIComponent(token)}`, { credentials: 'same-origin' });
  if (res.status === 404 || res.status === 400) return false;
  if (!res.ok) throw new Error(`whoami ${res.status}`);
  return true;
}

/** The phone's seat, remembered so a reload or a dropped connection rejoins. */
export interface SavedSeat {
  token: string;
  sessionId: string;
  code: string;
  table: number;
  role: string;
  name: string;
}
const SEAT_KEY = 'ns_seat';

export function loadSeat(): SavedSeat | null {
  try {
    const raw = localStorage.getItem(SEAT_KEY);
    return raw ? (JSON.parse(raw) as SavedSeat) : null;
  } catch {
    return null;
  }
}
export function saveSeat(seat: SavedSeat | null): void {
  try {
    if (seat) localStorage.setItem(SEAT_KEY, JSON.stringify(seat));
    else localStorage.removeItem(SEAT_KEY);
  } catch {
    /* private mode: the seat lasts as long as the tab */
  }
}
